// project imports
import {
  InspectionDTO,
  InspectionMemberDTO,
  InspectionStandardDTO,
  QuestionnaireDTO,
  UploadFileDTO,
} from "../../../common/types/common.dto.types";
import { ActionResponseType } from "../../../common/types/common.types";
import { ApiClient } from "../../../core/api/apiClient.service";
import { QuestionnaireSummaryParameterDTO } from "../QuestionnaireSummaryPage/QuestionnaireSummaryPage.types";
import { CopyInspectionStandardDTO } from "./SiteInspectionStartPage.types";

// client object for axios calls to backend
const client = new ApiClient();

/**
 *
 * @param file - file to be converted to base64
 * @description This function converts a file to base64 format using FileReader API.
 */
const toBase64 = async (file) =>
  await new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
    reader.onerror = (error) => reject(error);
  });

/**
 *
 * @param base64String - base64 string to be obfuscated
 * @description This function obfuscates a base64 string by shifting each character by 3 positions.
 * @returns the corresponding obfuscated base64 string.
 */
const obfuscateBase64ShiftEncoding = (base64String) => {
  const shift = 3; // Shift each character by 3 positions
  let obfuscatedString = "";

  for (let char of base64String) {
    obfuscatedString += String.fromCharCode(char.charCodeAt(0) + shift);
  }

  return obfuscatedString;
};

/**
 *@name cleanFileBeforeUpload
 * @param uploadedFile - file to be uploaded
 * @description This function prepares the file for upload by clearing unnecessary properties.
 * @returns the cleaned file.
 */
const cleanFileBeforeUpload = (uploadedFile: UploadFileDTO): UploadFileDTO => {
  if (uploadedFile) {
    uploadedFile = {
      ...uploadedFile,
      FilePreview: "",
      FileData: null,
    };
  }

  return uploadedFile;
};

export const getSiteInspectionQuestionList = async (
  id: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/GetInspectionQuestionByInspectionId/${id}`;
    const config = {
      headers: {
        responseType: "arraybuffer",
      },
    };

    let siteInspectionQuestion = {} as ActionResponseType;
    await client
      .get<ActionResponseType>(URL, config)
      .then((response: ActionResponseType) => {
        siteInspectionQuestion = response;
      })
      .catch((error) => {
        siteInspectionQuestion = {} as ActionResponseType;
      });
    return siteInspectionQuestion;
  } catch (error) {
    throw error;
  }
};

// export const getSiteInspectionQuestionList = async (
//   id: number
// ): Promise<ActionResponseType> => {
//   try {
//     const URL: string = `InspectionQuestion/GetInspectionQuestionByInspectionId/${id}`;
//     let siteInspectionQuestion = {} as ActionResponseType;
//     await client
//       .get<ActionResponseType>(URL)
//       .then((response: ActionResponseType) => {
//         siteInspectionQuestion = response;
//       })
//       .catch((error) => {
//         siteInspectionQuestion = {} as ActionResponseType;
//       });
//     return siteInspectionQuestion;
//   } catch (error) {
//     throw error;
//   }
// };

export const resumeInspectionStandard = async (
  inspectionStandardId: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `Questionnaire/ResumeInspectionStandard/${inspectionStandardId}`;
    let siteInspectionQuestion = {} as ActionResponseType;
    await client
      .get<ActionResponseType>(URL)
      .then((response: ActionResponseType) => {
        siteInspectionQuestion = response;
      })
      .catch((error) => {
        siteInspectionQuestion = {} as ActionResponseType;
      });
    return siteInspectionQuestion;
  } catch (error) {
    throw error;
  }
};

export const saveInspectionSiteTeamMember = async (
  Params: Array<InspectionMemberDTO>
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/SaveInspectionSiteTeamMember`;
    let inspectionSiteTeamMember = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, Params)
      .then((response) => {
        inspectionSiteTeamMember = response;
      })
      .catch((error) => {
        inspectionSiteTeamMember = {} as ActionResponseType;
        return error;
      });
    return inspectionSiteTeamMember;
  } catch (error) {
    throw error;
  }
};

export const copyInspectionStandard = async (
  Params: CopyInspectionStandardDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `Questionnaire/CopyInspectionStandard`;
    let inspectionStandard = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, Params)
      .then((response) => {
        inspectionStandard = response;
      })
      .catch((error) => {
        inspectionStandard = {} as ActionResponseType;
        return error;
      });
    return inspectionStandard;
  } catch (e) {
    throw e;
  }
};

export const completeInspectionStandard = async (
  siteInspectionStandardParam: QuestionnaireDTO
): Promise<ActionResponseType> => {
  try {
    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };
    const URL: string = `Questionnaire/SaveInspectionStandardStatus`;
    let status = {} as ActionResponseType;
    const formData = new FormData();

    for (const key in siteInspectionStandardParam) {
      if (key !== "UploadedFileList") {
        formData.append(key, siteInspectionStandardParam[key]);
      } else {
      }
    }
    if (siteInspectionStandardParam.UploadedFileList.length > 0) {
      siteInspectionStandardParam.UploadedFileList.forEach(
        (file: UploadFileDTO, index: number) => {
          formData.append(`ImageFiles`, file.File);
        }
      );
    }

    await client
      .post<ActionResponseType>(URL, siteInspectionStandardParam)
      .then((response) => {
        status = response;
      })
      .catch((error) => {
        status = {} as ActionResponseType;
        return error;
      });
    return status;
  } catch (error) {
    throw error;
  }
};

export const completeInspection = async (
  siteInspectionParam: InspectionDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/CompleteInspection`;
    let inspection = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, siteInspectionParam)
      .then((response) => {
        inspection = response;
      })
      .catch((error) => {
        inspection = {} as ActionResponseType;
        return error;
      });
    return inspection;
  } catch (error) {
    throw error;
  }
};

export const reOpenCompleteInspection = async (
  id: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/ReOpenCompleteInspection/${id}`;
    let siteInspectionQuestion = {} as ActionResponseType;
    await client
      .get<ActionResponseType>(URL)
      .then((response: ActionResponseType) => {
        siteInspectionQuestion = response;
      })
      .catch((error) => {
        siteInspectionQuestion = {} as ActionResponseType;
      });
    return siteInspectionQuestion;
  } catch (error) {
    throw error;
  }
};

export const reOpenInspectionStandard = async (
  Params: QuestionnaireSummaryParameterDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `QuestionnaireSummary/Reassess`;
    let data = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, Params)
      .then((response: ActionResponseType) => {
        data = response;
      })
      .catch((error) => {
        // console.log(error);
      });

    return data;
  } catch (error) {
    // console.log(error);
    throw error;
  }
};

export const saveAssignedGlobalSMEApi = async (
  inspectionStandard: InspectionStandardDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `Questionnaire/SaveGlobalSMEAssignment`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, inspectionStandard)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const getGlobalSMEAssignmentPopupDataApi = async (
  inspectionStandard: InspectionStandardDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `Questionnaire/GetGlobalSMEAssignmentPopupData`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, inspectionStandard)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const startOfflineInspectionStandardApi = async (
  inspectionStandard: InspectionStandardDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/StartOfflineInspectionStandard`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, inspectionStandard)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const checkIfInspectionStandardHasAlreadyStartedApi = async (
  inspectionStandardId: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/CheckIfInspectionStandardHasAlreadyStarted/${inspectionStandardId}`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .get<ActionResponseType>(URL)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const checkBeforeRevokeInspectionStandardApi = async (
  inspectionStandardId: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/CheckBeforeRevokeInspectionStandard/${inspectionStandardId}`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .get<ActionResponseType>(URL)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const revokeInspectionStandardApi = async (
  inspectionStandard: InspectionStandardDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/RevokeStartedOfflineInspectionStandard`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, inspectionStandard)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const checkBeforeDownloadInspectionStandardQuestionnaireApi = async (
  inspectionStandardId: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/CheckBeforeDownloadInspectionStandardQuestionnaire/${inspectionStandardId}`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .get<ActionResponseType>(URL)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const questionnaireFileUpload = async (
  uploadFile: UploadFileDTO
): Promise<ActionResponseType> => {
  try {
    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };
    const URL: string = "InspectionQuestion/UploadQuestionnaire";
    let status: ActionResponseType;

    uploadFile = cleanFileBeforeUpload(uploadFile);

    status = await toBase64(uploadFile.File).then((result) => {
      const base64String: string = result?.toString().split(",")[1];

      // shit data by 3
      const obfuscateBase64ShiftEncodingString =
        obfuscateBase64ShiftEncoding(base64String);
      // encrypt data by urlencoded data
      const urlEncodedString = encodeURIComponent(
        obfuscateBase64ShiftEncodingString
      );
      uploadFile.FileBase64 = urlEncodedString;
      uploadFile.File = null;
      return client
        .post<ActionResponseType>(URL, uploadFile, config)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          status = {} as ActionResponseType;
          return error;
        });
    });
    if (status) return status;
  } catch (error) {
    throw error;
  }
};

export const checkBeforeUploadAnswersApi = async (
  inspectionStandardId: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `InspectionQuestion/CheckBeforeUploadAnswers/${inspectionStandardId}`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .get<ActionResponseType>(URL)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};
